import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from "../../firebaseConfig";
import { useAuth } from '../../AuthContext';
import { useSubAgentAuth } from '../../pages/Clients/SubAgent/SubAgentAuthContext';
import { useAppSelector } from '../../redux/store';
import { fetchLeadStageAnalytics, LeadData } from './Function';
import getCurrentScreen from '../../Helper/GetScreen';
import { fetchDepartments } from '../../pages/Clients/Admin/Function/LeadFunctions';

interface UserOption {
  id: string;
  name: string;
  department?: string;
  departmentId?: string;
}

interface DepartmentOption {
  id: string;
  name: string;
}

interface CallLeadAnalyticsProps {
  settings: any;
}

const LeadAnalytics: React.FC<CallLeadAnalyticsProps> = ({ settings }) => {
  const { userData, isAdmin, isManager, isStaff, currentUser } = useAuth();
  const { currentSubAgent, subAgentData } = useSubAgentAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const customDatePopupRef = React.useRef<HTMLDivElement>(null);

  const [customDatePopupOpen, setCustomDatePopupOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filter, setFilter] = useState<boolean>(false);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<UserOption[]>([]);
  const [leadData, setLeadData] = useState<LeadData>();
  const [users, setUsers] = useState<UserOption[]>([]);

  // Additional state for toast and error handling
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [nodata, setNodata] = useState(false);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!userData?.companyId) return;

      try {
        // First fetch all users to get their department IDs
        let usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", userData?.companyId)
        );

        // Apply role-based filtering to users first
        if (isManager && currentScreen === 'Manager') {
          usersQuery = query(
            collection(db, "users"),
            where("companyId", "==", userData?.companyId),
            where("managerIds", "array-contains", currentUser?.uid)
          );
        } else if (isStaff && currentScreen === 'Staff') {
          usersQuery = query(
            collection(db, "users"),
            where("companyId", "==", userData?.companyId),
            where(documentId(), "==", currentUser?.uid)
          );
        }

        const usersSnapshot = await getDocs(usersQuery);
        const userDepartmentIds = new Set(
          usersSnapshot.docs
            .map(doc => doc.data().departmentId)
            .filter(deptId => deptId) // Remove null/undefined values
        );

        // Now fetch departments, but only include those that have staff
        const companyDepartmentsSnapshot = await getDocs(
          collection(db, `companies/${userData.companyId}/departments`)
        );
        
        const departmentsWithStaff = companyDepartmentsSnapshot.docs
          .filter(doc => userDepartmentIds.has(doc.id))
          .map(doc => ({
            id: doc.id,
            name: doc.data().department
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setDepartments(departmentsWithStaff);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchDepartments();
  }, [userData?.companyId, isManager, isStaff, currentScreen, currentUser?.uid]);

  // Handle department change
  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const deptId = e.target.value;
    setSelectedDepartmentId(deptId);

    if (deptId === 'all') {
      setSelectedUser([]);
    } else {
      // Filter users for selected department
      const deptUsers = users.filter(user => user.departmentId === deptId);
      setSelectedUser(deptUsers);
    }
  };

  // Handle user selection
  const handleSelectedUser = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'All Staff') {
      // If department is selected, include all users from that department
      if (selectedDepartmentId !== 'all') {
        const deptUsers = users.filter(user => user.departmentId === selectedDepartmentId);
        setSelectedUser(deptUsers);
      } else {
        setSelectedUser([]);
      }
      return;
    }
    // Single user selection
    setSelectedUser([{ id: e.target.value, name: '' }]);
  };

  
  const fetchAssignedToOptions = React.useCallback(async () => {
    if (!userData?.companyId) return;

    try {
      // Remove department filter from the query - fetch all users first
      let usersQuery = query(
        collection(db, "users"),
        where("companyId", "==", userData?.companyId)
      );

      // Rest of role-based filtering...
      if (isManager && currentScreen === 'Manager') {
        usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", userData?.companyId),
          where("managerIds", "array-contains", currentUser?.uid)
        );
      } else if (isStaff && currentScreen === 'Staff') {
        usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", userData?.companyId),
          where(documentId(), "==", currentUser?.uid)
        );
      }

      const usersSnapshot = await getDocs(usersQuery);
      const allUsers = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        department: doc.data().department, // Include department name for display
        departmentId: doc.data().departmentId // Include departmentId for filtering
      }));

      if (allUsers.length === 0) {
        setToastMessage("No users found in your team");
        setShowToast(true);
        setNodata(true);
        setUsers([]);
        setSelectedUser([]);
      } else {
        setUsers(allUsers);
        setNodata(false);
        // Update selected users based on department filter
        if (selectedDepartmentId !== 'all') {
          const departmentUsers = allUsers.filter(user => user.departmentId === selectedDepartmentId);
          setSelectedUser(departmentUsers);
        } else {
          setSelectedUser([]);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [userData?.companyId, isManager, isStaff, currentScreen, currentUser?.uid, selectedDepartmentId]);

  React.useEffect(() => {
    fetchAssignedToOptions();
  }, [fetchAssignedToOptions]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Filter users based on selected department
  const filteredUsers = selectedDepartmentId === 'all'
    ? users
    : users.filter(user => user.departmentId === selectedDepartmentId);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customDatePopupRef.current && !customDatePopupRef.current.contains(event.target as Node)) {
        setCustomDatePopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateRangeClick = () => {
    setCustomDatePopupOpen(!customDatePopupOpen);
  };

  // Initialize dates
  useEffect(() => {
    setStartDate(new Date());
    setEndDate(new Date());
  }, [userData?.companyId, users, currentScreen]);

  const handleDateRange = (date: Date | null, type: string) => {
    if (!date) return null;

    if (type === 'start') {
      date.setHours(5, 30, 0, 0);
    } else {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(5, 29, 59, 0);
      return nextDay.toISOString();
    }

    return date.toISOString();
  };

  const handleApplyFilters = () => {
    setFilter(true);
    setCustomDatePopupOpen(false);
  };

  // Fetch lead data
  useEffect(() => {
    if (users.length > 0) {
      fetchLeadStageAnalytics({
        db,
        userData: userData || subAgentData,
        startTime: handleDateRange(startDate, 'start') || null,
        endTime: handleDateRange(endDate, 'end') || null,
        users,
        currentScreen,
        selectedUser,
        settingsData: settings?.stages
      })
        .then((data) => { setLeadData(data); })
        .catch((error) => console.error("Error fetching Lead Data:", error));
    }
  }, [userData?.companyId, startDate, endDate, users, currentScreen, selectedUser, filter]);

  return (
    <div className="bg-white p-2 mx-auto">
      {/* Toast message */}
      {showToast && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-md shadow-lg z-50">
          {toastMessage}
        </div>
      )}
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 mb-6">
          <div
            className="flex items-center justify-center w-10 h-10 rounded-full"
            style={{
              background: "linear-gradient(180deg, #FFA782 0%, #FC583D 100%)",
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/desktop/step.svg`}
              alt="Icon"
              className="w-6 h-6"
            />
          </div>
          <h2
            className="text-xl text-black font-medium"
            style={{
              fontFamily: "Montserrat",
              lineHeight: "29.26px",
            }}
          >
            Lead Analytics
          </h2>
        </div>

        <div className="rounded px-4 py-2 flex items-center gap-5">
          {(currentScreen !== 'Staff' && currentSubAgent?.uid === undefined) && (
            <>
              {/* Department dropdown */}
              <select
                className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white"
                value={selectedDepartmentId}
                onChange={handleDepartmentChange}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>

              {/* Staff dropdown */}
              <select
                className="w-full sm:w-auto p-2 border border-cyan-600 text-sm rounded-lg bg-white"
                value={
                  selectedUser.length === 0 ? 'All Staff' :
                    selectedUser.length === users.length ? 'All Staff' :
                      selectedUser.length === 1 ? selectedUser[0].id :
                        'Multiple Selected'
                }
                onChange={handleSelectedUser}
              >
                <option value="All Staff">
                  {selectedDepartmentId !== 'all'
                    ? `All Staff (${departments.find(d => d.id === selectedDepartmentId)?.name || 'Selected Department'})`
                    : 'All Staff'
                  }
                </option>
                {filteredUsers.map(staff => (
                  <option key={staff.id} value={staff.id}>{staff.name}</option>
                ))}
              </select>
            </>
          )}

          {/* Date range picker */}
          <div className="relative">
            <div
              className="w-full sm:w-auto py-2 px-1 border border-cyan-600 text-sm rounded-lg bg-white cursor-pointer"
              onClick={handleDateRangeClick}
            >
              {startDate === null && endDate === null
                ? "Lifetime"
                : startDate && endDate
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : "Select Date Range"}
            </div>
            {customDatePopupOpen && (
              <div
                ref={customDatePopupRef}
                className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-[14px] font-normal leading-[20px] text-left">
                    Select Date Range
                  </h3>
                </div>
                <div className="mb-1">
                  <span className="text-[12px] font-normal leading-[16px] text-left">
                    <span className='font-medium'>Current: </span>
                    {startDate && endDate
                      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                      : "Select Date Range"}
                  </span>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button onClick={() => { setStartDate(new Date()); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 7))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 30))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 90))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                    {/* Lifetime filter */}
                    <button onClick={() => { setStartDate(null); setEndDate(null); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Lifetime</button>
                  </div>
                  <div
                    className="p-4 bg-white shadow-lg rounded-[12px]"
                    style={{ width: '100%', height: 'auto' }}
                  >
                    <DatePicker
                      selected={startDate}
                      startDate={startDate || undefined}
                      endDate={endDate || undefined}
                      selectsRange
                      onChange={(update) => {
                        const [start, end] = update;
                        setStartDate(start);
                        setEndDate(end);
                      }}
                      inline
                      className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                      maxDate={new Date()}
                    />
                  </div>
                </div>
                <div className="flex justify-between mt-2">
                  <button
                    className="px-8 py-2 text-white bg-black rounded-full"
                    onClick={() => setCustomDatePopupOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <h3 className="text-sm font-medium">
          Total Leads: {leadData?.totalLeads}
        </h3>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {(leadData?.stageMapArray || [])
          .sort((a, b) => {
            // No Stage comes first
            if (a.stage === 'No Stage') return -1;
            if (b.stage === 'No Stage') return 1;

            // All other stages sorted alphabetically
            return a.stage.localeCompare(b.stage);
          })
          .map((box, index) => (
            <div key={index} className="border border-cyan-500 rounded-2xl py-2">
              <h4 className="text-center font-sm font-semibold mb-2">{box.stage}</h4>
              <div className="h-px bg-cyan-500 w-full mb-2"></div>
              <div className="flex items-center justify-center gap-2">
                <div className="text-lg">{box.count}</div>
                <div className={`text-sm ${box.count > 0 ? 'text-green-500' : 'text-gray-500'}`}>
                  {box.percentage}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default LeadAnalytics;
